import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CampLocation } from "@/types/campLocation";
import { Plus, Upload, Trash2, Edit, MoveUp, MoveDown, Image } from "lucide-react";
import { Copy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PhotoUploadDialog } from "../components/PhotoUploadDialog";
import { PhotoEditDialog } from "../components/PhotoEditDialog";
import { PlaceholderPhotoDialog } from "../components/PlaceholderPhotoDialog";
import { Card } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { LocationPhoto } from "@/types/campPhoto";
import {
  prependPhotoToLocationVersion,
  removePhotoFromLocationVersion,
  reorderPhotosInLocationVersion,
} from "@/services/campPhotoService";
import { doc } from "firebase/firestore";
import { db } from "@/services/firebaseService";
import {
  parsePastedLocationPhotoJson,
  type PlainPastedLocationPhotoFields,
} from "@/utils/parsePastedLocationPhotoJson";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useCopyPhotos } from "../hooks/useCopyPhotos";
import { CopyPhotosModal } from "../components/CopyPhotosModal";
import { SearchProvidersModal } from "../components/SearchProvidersModal";
 

interface PhotosTabSectionProps {
  locationId: string;
  location: CampLocation;
  onSave: (location: CampLocation, changeDescription?: string) => Promise<void>;
  saving: boolean;
}

export const PhotosTabSection: React.FC<PhotosTabSectionProps> = ({
  locationId,
  location,
  onSave,
  saving
}) => {
  // Transform helpers for provider/credit display
  const photoProvider = (value?: string) => {
    const s = (value || "").trim();
    if (s === "Canva" || s === "from Canva") return "Canva";
    return s;
  };

  const { currentUser, isAdmin } = useAuth();
  const [photos, setPhotos] = useState<LocationPhoto[]>([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [placeholderDialogOpen, setPlaceholderDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [searchProvidersOpen, setSearchProvidersOpen] = useState(false);
  const [addFromJsonOpen, setAddFromJsonOpen] = useState(false);
  const [addFromJsonText, setAddFromJsonText] = useState("");
  const [addFromJsonError, setAddFromJsonError] = useState<string | null>(null);
  const [addFromJsonSubmitting, setAddFromJsonSubmitting] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  // Removed cache-busting state; no longer needed

  const {
    isModalOpen: isCopyModalOpen,
    isLoading: isCopyLoading,
    locationOptions: copyLocationOptions,
    canShowCopyButton,
    showCopyPhotosModal,
    closeCopyPhotosModal,
    executeCopyPhotos
  } = useCopyPhotos({ location });

  useEffect(() => {
    // Initialize photos from location data
    if (location && location.photos) {
      console.log("[PhotosTabSection] Location photos data:", location.photos);
      setPhotos(location.photos);
    } else {
      setPhotos([]);
    }
  }, [location]);

  const handleDeletePhoto = async () => {
    if (selectedPhotoIndex === null || !currentUser) return;
    
    try {
      setLoading(true);
      const photo = photos[selectedPhotoIndex];
      const campPhotoIdVal = (photo?.campPhotoId as any)?.id || (typeof photo?.campPhotoId === 'string' ? photo?.campPhotoId : undefined);
      const isPlaceholder = campPhotoIdVal === '_placeholder' || campPhotoIdVal === 'campPhotos/_placeholder';
      await removePhotoFromLocationVersion(
        locationId,
        currentUser.uid,
        selectedPhotoIndex
      );
      
      // Update local state
      const updatedPhotos = [...photos];
      updatedPhotos.splice(selectedPhotoIndex, 1);
      setPhotos(updatedPhotos);
      
      setDeleteDialogOpen(false);
      toast.success(isPlaceholder ? "Removed placeholder photo" : "Photo deleted successfully");
    } catch (error) {
      console.error("Error deleting photo:", error);
      toast.error("Failed to delete photo");
    } finally {
      setLoading(false);
      setSelectedPhotoIndex(null);
    }
  };

  const buildLocationPhotoFromPlainFields = (
    plain: PlainPastedLocationPhotoFields
  ): LocationPhoto & { ogPath?: string } => {
    const campPhotoId = doc(db, plain.campPhotoCollection, plain.campPhotoDocumentId);
    const locationPhoto: LocationPhoto & { ogPath?: string } = {
      path: plain.path,
      photoReference: plain.photoReference,
      yOffset: plain.yOffset,
      campPhotoId,
    };
    if (plain.caption !== undefined) locationPhoto.caption = plain.caption;
    if (plain.altText !== undefined) locationPhoto.altText = plain.altText;
    if (plain.permissionStatus) locationPhoto.permissionStatus = plain.permissionStatus;
    if (plain.permissionNote !== undefined) locationPhoto.permissionNote = plain.permissionNote;
    if (plain.permissionInternalNote !== undefined) {
      locationPhoto.permissionInternalNote = plain.permissionInternalNote;
    }
    if (plain.order !== undefined) locationPhoto.order = plain.order;
    if (plain.ogPath) locationPhoto.ogPath = plain.ogPath;
    return locationPhoto;
  };

  const handleAddFromJsonSubmit = async () => {
    if (!currentUser) {
      toast.error("You must be logged in");
      return;
    }
    setAddFromJsonError(null);
    setAddFromJsonSubmitting(true);
    try {
      const plain = parsePastedLocationPhotoJson(addFromJsonText.trim());
      const locationPhoto = buildLocationPhotoFromPlainFields(plain);
      await prependPhotoToLocationVersion(locationId, currentUser.uid, locationPhoto);
      toast.success("Photo inserted as first image");
      setAddFromJsonOpen(false);
      setAddFromJsonText("");
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to add photo";
      setAddFromJsonError(msg);
      toast.error(msg);
    } finally {
      setAddFromJsonSubmitting(false);
    }
  };

  const handleMovePhoto = async (index: number, direction: "up" | "down") => {
    if (!currentUser) return;
    
    // Can't move up if already at the top
    if (direction === "up" && index === 0) return;
    
    // Can't move down if already at the bottom
    if (direction === "down" && index === photos.length - 1) return;
    
    try {
      setLoading(true);
      
      // Create new order array
      const newOrder = [...Array(photos.length).keys()];
      
      if (direction === "up") {
        // Swap current with previous
        [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
      } else {
        // Swap current with next
        [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      }
      
      await reorderPhotosInLocationVersion(
        locationId,
        currentUser.uid,
        newOrder
      );
      
      // Update local state
      const updatedPhotos = newOrder.map(i => photos[i]);
      setPhotos(updatedPhotos);
      
      toast.success(`Photo moved ${direction}`);
    } catch (error) {
      console.error(`Error moving photo ${direction}:`, error);
      toast.error(`Failed to move photo ${direction}`);
    } finally {
      setLoading(false);
    }
  };

  // Removed: Break cache handler

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">Photos</h2>
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => setUploadDialogOpen(true)}
            disabled={saving || loading}
          >
            <Upload className="mr-2 h-4 w-4" /> Upload photo
          </Button>
          {isAdmin && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              onClick={() => setSearchProvidersOpen(true)}
              disabled={saving || loading}
            >
              <span>Search providers</span>
            </Button>
          )}
          {isAdmin && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setAddFromJsonError(null);
                setAddFromJsonOpen(true);
              }}
              disabled={saving || loading || addFromJsonSubmitting}
            >
              Add from JSON
            </Button>
          )}
          {canShowCopyButton && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              onClick={showCopyPhotosModal}
              disabled={saving || loading || isCopyLoading}
            >
              <Copy className="w-4 h-4" />
              <span>Copy from location...</span>
            </Button>
          )}
          {/* Removed: Break cache admin button */}
        </div>
      </div>
      
      {photos.length === 0 ? (
        <Card className="p-8 flex flex-col items-center justify-center text-center">
          <Image className="h-12 w-12 text-gray-400 mb-4" />
          <h3 className="font-medium text-lg mb-2">No photos yet</h3>
          <p className="text-gray-500 mb-6">Upload photos to showcase this location</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              onClick={() => setPlaceholderDialogOpen(true)}
              disabled={saving || loading}
              variant="outline"
            >
              <Image className="mr-2 h-4 w-4" /> Add placeholder photo
            </Button>
            <Button onClick={() => setUploadDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> Add first photo
            </Button>
            {isAdmin && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setAddFromJsonError(null);
                  setAddFromJsonOpen(true);
                }}
                disabled={saving || loading || addFromJsonSubmitting}
              >
                Add from JSON
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {photos.map((photo, index) => {
            const photoPath = (photo as any)?.path || "";
            const isWebp = /\.webp(\?|$)/i.test(photoPath);
            const is1200 = /__w1200\.webp(\?|$)/i.test(photoPath);
            const isWebp1200 = isWebp && is1200;
            
            // Debug log to see what's happening
            console.log(`Photo ${index}: path="${photoPath}", ogPath="${(photo as any)?.ogPath}", isWebp=${isWebp}, is1200=${is1200}, isWebp1200=${isWebp1200}`);
            
            return (
            <Card key={index} className={`overflow-hidden ${!isWebp1200 ? 'border-2' : ''}`} style={!isWebp1200 ? { borderColor: '#444444' } : {}}>
              <AspectRatio ratio={2/1} className="bg-gray-100">
                <img 
                  src={photo.path} 
                  alt={photo.altText || "Location photo"} 
                  className="object-cover w-full h-full"
                  style={{ 
                    objectPosition: `center ${photo.yOffset !== undefined ? `${50 + (photo.yOffset * 50)}%` : '50%'}` 
                  }}
                />
              </AspectRatio>
              <div className="p-4 space-y-2">
                {photo.caption && (
                  <p className="text-sm">{photo.caption}</p>
                )}
                {photo.photoReference && (
                  <p className="text-xs text-gray-500">{photoProvider(photo.photoReference)}</p>
                )}
                <div className="flex justify-between pt-2">
                  <div>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => {
                        setSelectedPhotoIndex(index);
                        setEditDialogOpen(true);
                      }}
                      disabled={loading}
                    >
                      <Edit className="h-4 w-4 mr-1" /> Edit
                    </Button>
                  </div>
                  <div className="space-x-1">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleMovePhoto(index, "up")} 
                      disabled={index === 0 || loading}
                    >
                      <MoveUp className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleMovePhoto(index, "down")} 
                      disabled={index === photos.length - 1 || loading}
                    >
                      <MoveDown className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => {
                        setSelectedPhotoIndex(index);
                        setDeleteDialogOpen(true);
                      }}
                      disabled={loading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
            );
          })}
        </div>
      )}
      
      {/* Upload Dialog */}
      <PhotoUploadDialog
        isOpen={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        locationId={locationId}
        onSuccess={() => {
          setUploadDialogOpen(false);
          // Photos will update via location data
        }}
      />
      
      {/* Placeholder Photo Dialog */}
      <PlaceholderPhotoDialog
        isOpen={placeholderDialogOpen}
        onClose={() => setPlaceholderDialogOpen(false)}
        locationId={locationId}
        onSuccess={() => {
          setPlaceholderDialogOpen(false);
          // Photos will update via location data  
        }}
      />
      
      {/* Edit Dialog */}
      {selectedPhotoIndex !== null && photos[selectedPhotoIndex] && (
        <PhotoEditDialog
          isOpen={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedPhotoIndex(null);
          }}
          photo={photos[selectedPhotoIndex]}
          photoIndex={selectedPhotoIndex}
          locationId={locationId}
          versionDocId={typeof (location as any)?.latestVersionId === 'string' ? (location as any)?.latestVersionId : (location as any)?.latestVersionId?.id}
          onSuccess={() => {
            setEditDialogOpen(false);
            setSelectedPhotoIndex(null);
            // Photos will update via location data
          }}
        />
      )}
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            {(() => {
              const p = selectedPhotoIndex !== null ? photos[selectedPhotoIndex] : null;
              const campPhotoIdVal = (p?.campPhotoId as any)?.id || (typeof p?.campPhotoId === 'string' ? p?.campPhotoId : undefined);
              const isPlaceholder = campPhotoIdVal === '_placeholder' || campPhotoIdVal === 'campPhotos/_placeholder';
              return (
                <>
                  <AlertDialogTitle>{isPlaceholder ? 'Remove placeholder photo?' : 'Delete photo?'}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {isPlaceholder 
                      ? 'This will remove the placeholder photo from the location. You can add it back later.'
                      : 'This action cannot be undone. This will permanently delete this photo from this location.'}
                  </AlertDialogDescription>
                </>
              );
            })()}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => {
                setDeleteDialogOpen(false);
                setSelectedPhotoIndex(null);
              }}
              disabled={loading}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeletePhoto}
              disabled={loading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {(() => {
                const p = selectedPhotoIndex !== null ? photos[selectedPhotoIndex] : null;
                const campPhotoIdVal = (p?.campPhotoId as any)?.id || (typeof p?.campPhotoId === 'string' ? p?.campPhotoId : undefined);
                const isPlaceholder = campPhotoIdVal === '_placeholder' || campPhotoIdVal === 'campPhotos/_placeholder';
                if (loading) return isPlaceholder ? 'Removing...' : 'Deleting...';
                return isPlaceholder ? 'Remove photo' : 'Delete photo';
              })()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Copy Photos Modal */}
      <CopyPhotosModal
        isOpen={isCopyModalOpen}
        onClose={closeCopyPhotosModal}
        onConfirm={executeCopyPhotos}
        locationOptions={copyLocationOptions}
        destinationLocationName={location?.name || "this location"}
        isLoading={isCopyLoading}
      />

      <SearchProvidersModal
        isOpen={searchProvidersOpen}
        onClose={() => setSearchProvidersOpen(false)}
        locationId={locationId}
        location={location}
        onImported={() => {
          setSearchProvidersOpen(false);
          window.dispatchEvent(new CustomEvent("photosUpdated", { detail: { locationId } }));
        }}
      />

      <Dialog
        open={addFromJsonOpen}
        onOpenChange={(open) => {
          setAddFromJsonOpen(open);
          if (!open) {
            setAddFromJsonText("");
            setAddFromJsonError(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Add from JSON</DialogTitle>
            <DialogDescription>
              Paste one photo object. It is inserted at the start of the photos list (first / header
              image). <code className="text-xs">campPhotoId</code> may use{" "}
              <code className="text-xs">referencePath</code> (for example{" "}
              <code className="text-xs">campPhotos/yourPhotoId</code>).
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={addFromJsonText}
            onChange={(e) => {
              setAddFromJsonText(e.target.value);
              setAddFromJsonError(null);
            }}
            placeholder='{ "path": "...", "campPhotoId": { "referencePath": "campPhotos/..." }, ... }'
            className="min-h-[220px] font-mono text-sm flex-1"
            disabled={addFromJsonSubmitting}
            aria-invalid={!!addFromJsonError}
          />
          {addFromJsonError ? (
            <p className="text-sm text-destructive" role="alert">
              {addFromJsonError}
            </p>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAddFromJsonOpen(false)}
              disabled={addFromJsonSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleAddFromJsonSubmit()}
              disabled={addFromJsonSubmitting || !addFromJsonText.trim()}
            >
              {addFromJsonSubmitting ? "Inserting..." : "Insert photo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
